/**
 * ⛔ THE SCOPE OF A BULK ACTION, STATED BEFORE THE CLICK — task-i4szfth3i6.
 *
 * An operator selected all, approved, and the Content Review queue still showed pending rows.
 * ⇒ NOTHING LIED. The result toast reads "Approved X of Y items", and `Y` is `totalRequested`,
 * which is what was SELECTED. A row the selection never contained cannot appear in that ratio,
 * so a fully-successful batch and a batch that missed 1,525 rows print the same sentence.
 * 🔑 The gap is between the SELECTION and the QUEUE — not between the RESULT and the SELECTION —
 * and nothing named it at the moment of acting.
 *
 * ⚠️ `filterTotal` is `pagination.total`, which is itself FILTER-SCOPED: the queue defaults to a
 * rolling 30-day window on `updated_at`. So this deliberately says "matching the current filter"
 * and NOT "in the queue" — the wider claim would be false, and a reassuring false number here is
 * worse than none.
 *
 * Returns null when there is nothing to warn about, so the caller renders nothing.
 */
export function bulkSelectionScopeNotice(
  selectedCount: number,
  filterTotal: number,
  selectionIsCurrent: boolean
): string | null {
  // ⛔⛔ THE SELECTION MUST BELONG TO THE FILTER WE ARE COUNTING AGAINST. Both codex legs blocked
  // #5614 on this, independently, and they were right: `selectedItems` SURVIVES a filter or site
  // change. The dashboard's `useEffect` on [filters] resets ONLY `allItemsSelected`, and its own
  // comment says "the 'all' now refers to different items" — so the code already knew about this
  // staleness and fixed one half of it. 16 of 18 `setFilters(...)` sites never clear the selection.
  // ⇒ Without this guard the badge compares a STALE selection against a FRESH total and reads
  // "90 of 100 … are NOT selected" when none of those 100 is selected and Approve will act on the
  // OLD rows. That is the badge lying, in the one situation it exists to protect.
  // ⇒ Silence is the correct output of a component that cannot know: "a reassuring wrong number
  // here is worse than none."
  if (!Number.isFinite(selectedCount) || !Number.isFinite(filterTotal)) return null;
  if (selectedCount <= 0) return null;
  // ⛔⛔ A STALE SELECTION MUST SPEAK, NOT GO SILENT. Suppressing the badge here was the first fix
  // and it was wrong: when the two counts COINCIDE — select 100, switch to a disjoint filter also
  // holding 100 — the `filterTotal <= selectedCount` guard below would suppress it anyway, and the
  // operator approves a completely disjoint cohort with NO warning at all. That is strictly worse
  // than the wrong number the guard was added to prevent.
  // 🔑 The honest output of a component that cannot know is not silence; it is saying that it
  // cannot know. So a mismatch reports WHAT WILL ACTUALLY BE APPROVED rather than a count of rows
  // on screen that have nothing to do with it.
  // ⚠️ This makes the situation LEGIBLE. It does not prevent it — a stale selection can still be
  // approved, exactly as on `next` today. That is filed separately and is not fixed here.
  if (!selectionIsCurrent) {
    return `${selectedCount} selected under a previous filter — the bulk action will apply to those rows, not to what is shown`;
  }
  // `>=` not `>`: an equal count means the selection already covers the filtered set, and a
  // "0 of N are NOT selected" badge would be noise that trains the operator to ignore this.
  if (filterTotal <= selectedCount) return null;
  // ⛔⛔ "AT LEAST", AND THE WORD IS LOAD-BEARING. codex-reviewer [blocking] at f3a35c8d0: this
  // subtraction assumes every selected id is still IN the filtered set. It need not be — another
  // process can approve your 25 pending rows, and then the filter signature is unchanged while
  // their MEMBERSHIP is not. No signature can detect that, because nothing on OUR side changed.
  //
  // ⇒ BUT THE NUMBER IS A LOWER BOUND, AND IT IS ALWAYS A TRUE ONE:
  //       unselected_true = filterTotal - |selected ∩ filter|
  //       |selected ∩ filter| <= selectedCount        (a selection cannot over-intersect)
  //   ⇒   unselected_true   >= filterTotal - selectedCount
  //   The figure can be too LOW. It can never be too HIGH.
  //
  // ⇒ So the defect is the FALSE PRECISION, not the number. "At least N" is provably true in every
  // case including the broken one, keeps the figure that makes the badge useful, and costs a string
  // rather than a round-trip. The EXACT count needs an intersection against the live filter and is
  // filed separately (task-q9zko7j8e0) — real engineering, not a wording change.
  // ⚠️ Do not "simplify" this back to an exact claim. The bound is what makes the sentence true.
  return `At least ${filterTotal - selectedCount} of ${filterTotal} matching the current filter are NOT selected`;
}

/**
 * ⛔ THE SCOPE-CHANGING FILTER FIELDS ONLY — PAGING AND SORTING ARE NOT SCOPE CHANGES.
 *
 * `setFilters(prev => ({...prev, page}))` means PAGING IS A FILTER CHANGE in this dashboard. So a
 * naive "the filters object changed ⇒ the selection is stale" test would blank the badge on every
 * page turn — and worse, it would treat a CROSS-PAGE selection (the one Jaume used to approve 2,628
 * items in a single action) as stale the moment he turned a page, which it is not.
 *
 * ⇒ The signature therefore covers only fields that change WHICH ROWS MATCH, and deliberately omits
 * `page` / `limit` / `sortBy` / `sortOrder`. The same PRESENTATION_ONLY split already exists in
 * ContentReviewDashboard.selectAllFilterParity.test.ts; this mirrors it rather than inventing a
 * second opinion about which fields are presentation.
 *
 * ⚠️ Unknown/new keys are INCLUDED by construction: the signature is built from a sorted scan of the
 * object minus the presentation keys, so a filter added later invalidates a stale selection by
 * default. Failing closed is the right direction — a missed invalidation shows a wrong number.
 */
const PRESENTATION_ONLY = new Set(['page', 'limit', 'sortBy', 'sortOrder', '_t']);

// ⚠️ Takes `object`, not `Record<string, unknown>`: `QueueFilters` is an INTERFACE, and TypeScript
// gives interfaces no implicit index signature, so the concrete filter type is not assignable to a
// Record. Widening here keeps the call sites clean without weakening `QueueFilters` itself.
export function selectionScopeSignature(filters: object | null | undefined): string {
  if (!filters || typeof filters !== 'object') return '';
  const rec = filters as Record<string, unknown>;
  return Object.keys(rec)
    .filter(k => !PRESENTATION_ONLY.has(k))
    .sort()
    .map(k => `${k}=${JSON.stringify(rec[k]) ?? 'null'}`)
    .join('&');
}

/**
 * ⛔⛔ WHICH SCOPE A NEW SELECTION BELONGS TO — AND WHY IT IS NOT ALWAYS THE CURRENT ONE.
 *
 * `ContentQueue` builds each change by COPYING the existing selection and adding/removing one id.
 * So after: select 10 under filter A → switch to B → tick one B row, `newSelected` holds 11 ids of
 * which 10 are invisible A rows.
 * ⇒ Stamping that set with B relabels the stale ten as belonging to the visible filter. The badge
 * then reports 89-of-100 unselected when the truth is 99-of-100, AND the bulk action still targets
 * 10 rows the operator cannot see — the exact class of misleading output this module exists to stop.
 *
 * ⚠️ The alternative remedy — DISCARD the stale ids on a scope change — was not taken: silently
 * throwing away rows an operator deliberately selected is destructive of intent, and it is a product
 * decision rather than a review fix. Keeping the combined set marked stale preserves the selection
 * AND keeps the warning truthful.
 */
/**
 * ⛔⛔ THE SELECTION'S SCOPE IS A STATE, NOT A STRING — AND THAT IS THE WHOLE FIX.
 *
 * Two versions of this shipped and both were blocked, for the same reason in opposite directions:
 * the first stamped the CURRENT scope onto everything, the second RETAINED the PREVIOUS one. Both
 * are single-value models, and the defect is in neither choice — **one value cannot represent a
 * selection that has two.** Whichever value is stored, some navigation path makes it look right
 * (A → B → add → back to A was the one that did).
 *
 * ⇒ So the mixed state is admitted into the TYPE rather than encoded into a value. A third version
 * using a NUL-byte sentinel string would have worked, but its safety rested on
 * `selectionScopeSignature` never emitting NUL — a coupling between two functions with nothing
 * enforcing it, which is the "written twice, agreement unenforced" hazard in miniature.
 *
 * Same lesson offering-dev reached independently on `c0dd4aa88` today, on a different surface:
 * a zero rowCount was two opposite facts wearing one number, and the fix was to CLASSIFY the
 * outcome rather than choose a cleverer number.
 */
export type SelectionScope =
  | { kind: 'none' }                            // nothing selected
  | { kind: 'current'; signature: string }      // wholly selected under one filter set
  | { kind: 'mixed' };                          // rows from more than one — sticky until cleared

export const NO_SELECTION: SelectionScope = { kind: 'none' };

/** Is this selection wholly a subset of the filter set on screen? */
export function isSelectionCurrent(scope: SelectionScope, currentSignature: string): boolean {
  return scope.kind === 'current' && scope.signature === currentSignature;
}

export function nextSelectionScope(
  previousScope: SelectionScope,
  hadSelection: boolean,
  nowHasSelection: boolean,
  currentSignature: string
): SelectionScope {
  // Nothing selected: there is no cohort to describe, so no scope to carry.
  if (!nowHasSelection) return NO_SELECTION;
  // First selection since the last clear — it can only belong to the filter on screen.
  if (!hadSelection) return { kind: 'current', signature: currentSignature };
  // The existing selection already belongs to this filter; adding to it keeps it current.
  if (isSelectionCurrent(previousScope, currentSignature)) {
    return { kind: 'current', signature: currentSignature };
  }
  // ⛔⛔ OTHERWISE ROWS FROM DIFFERENT SCOPES ARE BEING COMBINED, AND ONE SLOT CANNOT DESCRIBE THAT.
  // Returning `previousScope` here was the second blocking finding: select 10 under A, switch to B,
  // add one B row (set is now MIXED, labelled A), then RETURN TO A — the labels match again and all
  // 11 read as current, while one of them is a B row invisible under A.
  // ⇒ A mixed selection gets a sentinel that can never equal any real signature, so it stays stale
  // until the selection is emptied or wholly replaced. A single slot cannot represent "partly here";
  // it can represent "not simply here", and that is the honest thing for it to say.
  return { kind: 'mixed' };
}

/**
 * ⛔⛔ CROSS-SITE EXECUTION — A DIFFERENT ANIMAL FROM EVERYTHING ELSE IN THIS FILE.
 *
 * Every other helper here decides whether a WARNING is accurate. This one decides whether the
 * ACTION is. codex-reviewer-2 [blocking] at 4737d5e57: the bulk handlers send the retained ids with
 * the CURRENT `selectedSite.id`, so after a site switch the ids reach a site that does not own them.
 *
 * ✅ MEASURED SEVERITY, because it decides how bad this is and it was not safe to assume (db2,
 * read-only, 2026-09-01). The failure is LOUD, and loud BY CONSTRUCTION rather than by luck:
 *   · `aeo_page_generations.id` is a uuid PK defaulting to uuid_generate_v4() — globally unique, so
 *     a site-A id cannot exist in site B.
 *   · `approveContent` looks up `(id = $1 OR source_id = $1::text) AND site_id = $2` and THROWS
 *     "Product AEO not found" on a miss — every statement in that path is site-scoped.
 *   · `source_id` is uuid-shaped on 91,804 of 91,804 product rows and distinct on all 91,804, so the
 *     second branch is globally unique too.
 * ⇒ Both branches are globally unique, so `AND site_id = $2` can only ever REJECT — it can never
 * redirect a lookup to a different row. A cross-site batch therefore fails wholly and visibly
 * (`failed`, `success:false`, and the toast names the count) rather than approving a cohort nobody
 * selected. That is why this is a refusal and not an emergency.
 *
 * ⚠️ It is still a refusal. "Loud" means the operator learns afterwards; declining means the wrong
 * call is never made. And the severity argument rests on a uniqueness property of the data, which a
 * future import could weaken without anything here noticing.
 */
export type SelectionSite =
  | { kind: 'none' }
  | { kind: 'single'; siteId: string | null }
  | { kind: 'crossSite' };

export const NO_SELECTION_SITE: SelectionSite = { kind: 'none' };

/**
 * Mirrors nextSelectionScope deliberately — same four inputs, same latch. Once rows from two sites
 * are combined there is no safe site to execute against, and unlike the `mixed` scope latch (a
 * narrow false positive, see the PR discussion) latching here is unambiguously correct: no choice of
 * site would be right for a set spanning two.
 */
export function nextSelectionSite(
  previousSite: SelectionSite,
  hadSelection: boolean,
  nowHasSelection: boolean,
  currentSiteId: string | null
): SelectionSite {
  if (!nowHasSelection) return NO_SELECTION_SITE;
  if (!hadSelection) return { kind: 'single', siteId: currentSiteId };
  if (previousSite.kind === 'single' && previousSite.siteId === currentSiteId) return previousSite;
  if (previousSite.kind === 'none') return { kind: 'single', siteId: currentSiteId };
  return { kind: 'crossSite' };
}

/**
 * The refusal reason, or null when the action may proceed. Returning the REASON rather than a
 * boolean is the point: the operator has to be told which site the selection belongs to, or a
 * refusal is indistinguishable from a broken button — the same lesson as the run-guard that
 * disabled batch regeneration for 45 minutes with no explanation.
 */
export function crossSiteRefusal(
  selectionSite: SelectionSite,
  currentSiteId: string | null
): string | null {
  if (selectionSite.kind === 'none') return null;
  if (selectionSite.kind === 'crossSite') {
    return 'This selection contains rows from more than one site. Clear it and select again before running a bulk action.';
  }
  if (selectionSite.siteId !== currentSiteId) {
    return 'This selection was made on a different site. Switch back to that site, or clear the selection, before running a bulk action.';
  }
  return null;
}

/**
 * ⛔⛔ A RESPONSE DESCRIBES THE SCOPE IT WAS REQUESTED UNDER, NOT THE SCOPE IT ARRIVES INTO.
 *
 * Both codex legs [blocking] at 44dd530e7, independently. "Select all across pages" issues an async
 * request; if the operator switches site before it resolves, the response installs the PREVIOUS
 * site's ids into a queue showing the new one. Clearing the selection at the moment of the switch
 * cannot help — the guard is synchronous and the hazard is asynchronous, so the request was already
 * in flight when the guard ran.
 *
 * ⇒ The remedy both legs propose, and the same one this PR has now reached four times from different
 * directions: carry the scope WITH THE DATA instead of inferring it from the current moment.
 *
 * Trivial as a function, and that is the point — it is the DECISION, extracted so it can be tested
 * and mutated. The wiring it depends on (that the live value is read from a ref rather than the
 * handler's own stale closure) is what a source assertion has to cover, and it is stated as such.
 */
export function staleResponse(
  requestedSiteId: string | null,
  liveSiteId: string | null
): boolean {
  return requestedSiteId !== liveSiteId;
}


/**
 * The FULL filter signature, including presentation keys.
 *
 * ⚠️ DELIBERATELY DIFFERENT FROM selectionScopeSignature, WHICH EXCLUDES THEM. That exclusion is
 * right for a SELECTION — paging does not change which rows you picked. It is wrong for a RESPONSE:
 * a page-1 queue response landing after a page-2 request paints page 1's rows under page 2's
 * pagination, and `page` is exactly the key that tells them apart. Two questions, two signatures;
 * using one for both would silently drop the out-of-order paging case.
 */
export function requestSignature(filters: object | null | undefined): string {
  if (!filters) return '';
  return Object.entries(filters as Record<string, unknown>)
    .map(([k, v]) => [k, JSON.stringify(v ?? null)] as const)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
}

/**
 * ⛔⛔ SCOPE EQUIVALENCE IS NOT REQUEST IDENTITY — AND THIS ANSWERS THE SECOND QUESTION FOR BOTH
 * THE SPINNER *AND* THE DATA. Renamed from `ownsLoadingFlag` at 944f8bf2f: that name described one
 * USE of the property rather than the property, and the narrow name is part of why I only applied
 * it to spinner cleanup. codex-reviewer [blocking]: A1 starts, the operator visits B and returns to
 * A, A2 finishes, A1 lands and OVERWRITES A2 — and every scope guard PASSES, correctly, because
 * A1's scope genuinely is the current scope. Two plain refreshes do it with no navigation at all.
 * ⇒ A signature says "is this the same KIND of request". Only a generation says "is this the same
 * request", and painting needs the second.
 *
 * ⛔⛔ SCOPE-STALENESS AND SUPERSESSION ARE DIFFERENT QUESTIONS, AND I COLLAPSED THEM.
 *
 * codex-reviewer [blocking] at 76f742db2. Guarding the `finally` with staleResponse was right for
 * three of four loads and WRONG for the fourth, for a reason that is invisible until you name the
 * asymmetry: loadQueue / loadMetrics / loadCategories are driven by EFFECTS, so a scope change
 * always starts a replacement request that will own and clear the spinner. "Select all across
 * pages" is USER-INITIATED — nothing replaces it. So a stale select-all response returned without
 * clearing `loadingAllIds`, and the spinner stayed up forever with the selection controls disabled.
 *
 * ⇒ TWO QUESTIONS, TWO GUARDS:
 *     · may this response PAINT ITS DATA?     -> staleResponse(scope)     — is the scope still live?
 *     · must this response CLEAR THE SPINNER? -> ownsLoadingFlag(gen)     — am I the latest request?
 *   A superseded request must not paint, and must not clear a spinner it no longer owns. A response
 *   that is merely out-of-scope is still the latest request of its operation, so it MUST clear.
 *
 * 🔑 The general form, which is the part worth keeping: A GUARD THAT RETURNS EARLY HAS NOT
 * NECESSARILY STOPPED DOING THINGS. `return` still runs `finally`, and the cleanup it skips or
 * performs is a separate decision from the one the guard was written to make.
 */
export function isLatestRequest(requestGeneration: number, latestGeneration: number): boolean {
  return requestGeneration === latestGeneration;
}
